import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, Plus, Settings, Mail } from 'lucide-react';
import { Spinner } from '../../ui';
import { Campaign } from '../../../types/campaign';
import { CAMPAIGN_STATUS_COLORS } from '../../../constants/campaignStatus';

interface SidebarEmailSectionProps {
  campaigns: Campaign[];
  isExpanded: boolean;
  isLoading: boolean;
  onToggleSection: () => void;
  onOpenEmailSettings: () => void;
  onNewCampaign?: () => void;
}

export const SidebarEmailSection: React.FC<SidebarEmailSectionProps> = ({
  campaigns,
  isExpanded,
  isLoading,
  onToggleSection,
  onOpenEmailSettings,
  onNewCampaign,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div data-tour="sidebar-campaigns" className="mt-2.5">
      <div className="px-4 mb-1.5 flex items-center justify-between">
        <button
          onClick={onToggleSection}
          className="flex items-center gap-1.5 group"
        >
          <ChevronRight
            className={`w-2.5 h-2.5 text-neutral-400 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
          />
          <h3 className="text-2xs font-semibold uppercase tracking-wider text-neutral-400 group-hover:text-neutral-600 transition">
            Outreach Campaigns
          </h3>
          {campaigns.length > 0 && (
            <span className="text-2xs text-neutral-400">
              {campaigns.length}
            </span>
          )}
        </button>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onNewCampaign ? onNewCampaign() : navigate('/campaigns')}
            className="p-1 rounded text-neutral-500 hover:bg-neutral-200/60 transition"
            title="New campaign"
          >
            <Plus className="w-3 h-3" />
          </button>
          <button
            onClick={onOpenEmailSettings}
            className="p-1 rounded text-neutral-500 hover:bg-neutral-200/60 transition"
            title="Email settings"
          >
            <Settings className="w-3 h-3" />
          </button>
        </div>
      </div>

      <div
        className="grid transition-[grid-template-rows] duration-200 ease-out"
        style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden min-h-0">
          <div className="px-3 space-y-0.5 pb-1">
            {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Spinner size="sm" />
            </div>
          ) : campaigns.length === 0 ? (
            <button
              onClick={() => onNewCampaign ? onNewCampaign() : navigate('/campaigns')}
              className="flex items-center gap-2 py-2 px-2.5 rounded-lg hover:bg-neutral-100/80 transition w-full text-left group"
            >
              <Mail className="w-3.5 h-3.5 text-neutral-400 group-hover:text-blue-500 transition" />
              <span className="text-2xs text-neutral-400 group-hover:text-blue-500 transition">
                Send first campaign
              </span>
            </button>
          ) : (
            <>
              {campaigns.map((campaign) => {
                const isSelected = location.pathname === `/campaigns/${campaign.id}`;
                const statusColors = CAMPAIGN_STATUS_COLORS[campaign.status] || CAMPAIGN_STATUS_COLORS.draft;
                return (
                  <button
                    key={campaign.id}
                    onClick={() => navigate(`/campaigns/${campaign.id}`)}
                    className={`w-full flex items-center gap-2.5 py-2 px-2.5 rounded-lg transition ${
                      isSelected ? 'bg-white' : 'hover:bg-neutral-200/60'
                    }`}
                  >
                    <div className="relative flex-shrink-0">
                      <Mail
                        className={`w-4 h-4 ${
                          isSelected ? 'text-neutral-900' : 'text-neutral-500'
                        }`}
                      />
                      <div className={`absolute -top-0.5 -right-0.5 w-2 h-2 ${statusColors.dot} rounded-full`} />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <span
                        className={`text-13 truncate block ${
                          isSelected ? 'text-neutral-900 font-medium' : 'text-neutral-600'
                        }`}
                      >
                        {campaign.name}
                      </span>
                      <span className="text-2xs text-neutral-400 truncate block">
                        {campaign.recipients_count} recipients
                      </span>
                    </div>
                  </button>
                );
              })}
              <button
                onClick={() => navigate('/campaigns')}
                className="w-full flex items-center justify-center gap-1.5 py-1.5 text-2xs font-medium text-neutral-400 hover:text-neutral-600 rounded-md transition"
              >
                View all
              </button>
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};
